import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { pursuits } from '../../data/sampleData';
import { ListIcon, LayoutIcon, PlusIcon, SearchIcon, FilterIcon, MoreHorizontalIcon } from 'lucide-react';
type ViewMode = 'board' | 'list';
const PursuitCard: React.FC<{
  pursuit: (typeof pursuits)[0];
}> = ({
  pursuit
}) => {
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'green';
      case 'Pending':
        return 'yellow';
      case 'Won':
        return 'indigo';
      case 'Lost':
        return 'red';
      default:
        return 'gray';
    }
  };
  return <div className="bg-white border border-gray-6 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-433">
      <h3 className="text-base font-semibold text-gray-12 mb-1" data-id="element-434">
        {pursuit.name}
      </h3>
      <p className="text-sm text-gray-11 mb-3" data-id="element-435">{pursuit.client}</p>
      <div className="flex justify-between text-xs text-gray-10 mb-2" data-id="element-436">
        <span data-id="element-437">{pursuit.value}</span>
        <span data-id="element-438">{pursuit.deadline}</span>
      </div>
      <div className="flex justify-end" data-id="element-439">
        <Badge variant={getStatusBadgeVariant(pursuit.status)} data-id="element-440">
          {pursuit.status}
        </Badge>
      </div>
    </div>;
};
export const PursuitsPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const activeItems = pursuits.filter(pursuit => pursuit.status === 'Active');
  const pendingItems = pursuits.filter(pursuit => pursuit.status === 'Pending');
  const wonItems = pursuits.filter(pursuit => pursuit.status === 'Won');
  const lostItems = pursuits.filter(pursuit => pursuit.status === 'Lost');
  return <div data-id="element-441">
      <div className="flex justify-between items-center mb-8" data-id="element-442">
        <h1 className="text-2xl font-bold text-gray-12" data-id="element-443">Pursuits</h1>
        <div className="flex items-center space-x-3" data-id="element-444">
          <div className="flex bg-gray-3 rounded-md p-1" data-id="element-445">
            <button className={`p-1.5 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-12' : 'text-gray-11'}`} onClick={() => setViewMode('list')} data-id="element-446">
              <ListIcon size={18} data-id="element-447" />
            </button>
            <button className={`p-1.5 rounded-md ${viewMode === 'board' ? 'bg-white shadow-sm text-gray-12' : 'text-gray-11'}`} onClick={() => setViewMode('board')} data-id="element-448">
              <LayoutIcon size={18} data-id="element-449" />
            </button>
          </div>
          <Button icon={<PlusIcon size={16} data-id="element-451" />} data-id="element-450">New Pursuit</Button>
        </div>
      </div>
      {viewMode === 'board' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-452">
          <div data-id="element-453">
            <div className="flex items-center justify-between mb-4" data-id="element-454">
              <h2 className="text-sm font-semibold text-gray-11 uppercase" data-id="element-455">
                Active ({activeItems.length})
              </h2>
            </div>
            <div className="space-y-4" data-id="element-456">
              {activeItems.map(pursuit => <PursuitCard key={pursuit.id} pursuit={pursuit} data-id="element-457" />)}
            </div>
          </div>
          <div data-id="element-458">
            <div className="flex items-center justify-between mb-4" data-id="element-459">
              <h2 className="text-sm font-semibold text-gray-11 uppercase" data-id="element-460">
                Pending ({pendingItems.length})
              </h2>
            </div>
            <div className="space-y-4" data-id="element-461">
              {pendingItems.map(pursuit => <PursuitCard key={pursuit.id} pursuit={pursuit} data-id="element-462" />)}
            </div>
          </div>
          <div data-id="element-463">
            <div className="flex items-center justify-between mb-4" data-id="element-464">
              <h2 className="text-sm font-semibold text-gray-11 uppercase" data-id="element-465">
                Won ({wonItems.length})
              </h2>
            </div>
            <div className="space-y-4" data-id="element-466">
              {wonItems.map(pursuit => <PursuitCard key={pursuit.id} pursuit={pursuit} data-id="element-467" />)}
            </div>
          </div>
          <div data-id="element-468">
            <div className="flex items-center justify-between mb-4" data-id="element-469">
              <h2 className="text-sm font-semibold text-gray-11 uppercase" data-id="element-470">
                Lost ({lostItems.length})
              </h2>
            </div>
            <div className="space-y-4" data-id="element-471">
              {lostItems.map(pursuit => <PursuitCard key={pursuit.id} pursuit={pursuit} data-id="element-472" />)}
            </div>
          </div>
        </div>}
      {viewMode === 'list' && <div data-id="element-473">
          <div className="flex justify-between mb-4" data-id="element-474">
            <div className="flex items-center space-x-4" data-id="element-475">
              <div className="relative" data-id="element-476">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-477">
                  <SearchIcon size={16} className="text-gray-10" data-id="element-478" />
                </div>
                <input type="text" placeholder="Search pursuits..." className="pl-10 pr-4 py-2.5 h-10 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent w-64" data-id="element-479" />
              </div>
              <div className="flex items-center space-x-2" data-id="element-480">
                <Button variant="secondary" size="sm" icon={<FilterIcon size={14} data-id="element-482" />} data-id="element-481">
                  Status
                </Button>
                <Button variant="secondary" size="sm" data-id="element-483">
                  Client
                </Button>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-6 rounded-lg overflow-hidden" data-id="element-484">
            <table className="min-w-full divide-y divide-gray-6" data-id="element-485">
              <thead className="bg-gray-2" data-id="element-486">
                <tr data-id="element-487">
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-488">
                    Pursuit Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-489">
                    Client
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-490">
                    Value
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-491">
                    Deadline
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-492">
                    Status
                  </th>
                  <th scope="col" className="relative px-4 py-3" data-id="element-493">
                    <span className="sr-only" data-id="element-494">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-6" data-id="element-495">
                {pursuits.map(pursuit => <tr key={pursuit.id} className="hover:bg-gray-2 transition-colors" data-id="element-496">
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-497">
                      <div className="text-sm font-medium text-gray-12" data-id="element-498">
                        {pursuit.name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-499">
                      <div className="text-sm text-gray-11" data-id="element-500">
                        {pursuit.client}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-501">
                      <div className="text-sm text-gray-11" data-id="element-502">
                        {pursuit.value}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-503">
                      <div className="text-sm text-gray-11" data-id="element-504">
                        {pursuit.deadline}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-505">
                      <Badge variant={pursuit.status === 'Active' ? 'green' : pursuit.status === 'Pending' ? 'yellow' : pursuit.status === 'Won' ? 'indigo' : 'red'} data-id="element-506">
                        {pursuit.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-507">
                      <button className="text-gray-11 hover:text-gray-12" data-id="element-508">
                        <MoreHorizontalIcon size={16} data-id="element-509" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
};